import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeesAPI, exportAPI, lookupsAPI, myAccountsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import RequireCap from '../components/auth/RequireCap';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { SearchableCombobox } from '../components/SearchableCombobox';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Plus, Search, Users, Filter, Download, ArrowUp, ArrowDown, ArrowUpDown, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

// Same option sets the employee record itself offers, so a bulk change can't
// invent a value the detail form would then show as free text.
const POST_TYPES = ['Armed Security Guard', 'Clerical', 'GSOC Operator', 'None', 'Peace Officer', 'Salary', 'Shift Supervisor', 'Special Police Officer', 'Unarmed Security Officer'];
const CLASSIFICATIONS = ['1099', 'Clerical', 'Executive', 'None', 'Service Worker', 'Supervision', 'W-2'];
const NO_CHANGE = '__no_change__';

// Hire date is stored as a plain YYYY-MM-DD string; parse the parts rather than
// `new Date(str)`, which reads a bare date as UTC and shifts it a day west of GMT.
const formatHireDate = (raw) => {
  const [y, m, d] = (raw || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return raw || '-';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const tenureLabel = (raw) => {
  const [y, m, d] = (raw || '').slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  const now = new Date();
  let months = (now.getFullYear() - y) * 12 + (now.getMonth() - (m - 1));
  if (now.getDate() < d) months -= 1;
  if (months < 0) return 'starts soon';
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (!years) return `${rem} mo`;
  return rem ? `${years} yr ${rem} mo` : `${years} yr`;
};

export default function Employees() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortKey, setSortKey] = useState('last_name');
  const [sortDir, setSortDir] = useState('asc');
  // Bulk site assignment (2026-06 Site Teams)
  const [clientList, setClientList] = useState([]);
  const [selected, setSelected] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignClientIds, setAssignClientIds] = useState([]);
  const [bulkFields, setBulkFields] = useState({});
  const [locationList, setLocationList] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [assigning, setAssigning] = useState(false);
  // Team-scoped users see only employees at the client accounts they supervise.
  // With zero coverage the list is legitimately empty — explain that instead of
  // showing a bare table, which reads like a bug.
  const [coverage, setCoverage] = useState(null);

  useEffect(() => {
    loadData();
    lookupsAPI.clients({ is_active: true }).then(r => setClientList(r.data || [])).catch(() => setClientList([]));
    lookupsAPI.locations().then(r => setLocationList(r.data || [])).catch(() => setLocationList([]));
    employeesAPI.companies().then(r => setCompanyOptions(r.data || [])).catch(() => setCompanyOptions([]));
    myAccountsAPI.coverage().then(r => setCoverage(r.data)).catch(() => setCoverage(null));
  }, []);

  const noCoverage = Boolean(coverage?.is_team_scoped && coverage.client_count === 0);

  const clientName = (cid) => clientList.find(x => x.id === cid)?.name || null;

  const toggleSelected = (empId) => setSelected(prev => (
    prev.includes(empId) ? prev.filter(x => x !== empId) : [...prev, empId]
  ));

  const setBulkField = (field, value) => setBulkFields(prev => {
    const next = { ...prev };
    if (value === NO_CHANGE) delete next[field]; else next[field] = value;
    return next;
  });

  const FIELD_LABELS = {
    location_id: 'location / region', company: 'company',
    post_type: 'employee type', classification_description: 'classification',
  };
  const changeSummary = [
    ...Object.keys(bulkFields).map(f => FIELD_LABELS[f]),
    ...(assignClientIds.length > 0
      ? [`${assignClientIds.length} client account${assignClientIds.length === 1 ? '' : 's'}`] : []),
  ];

  const submitAssign = async () => {
    setAssigning(true);
    try {
      const { data } = await employeesAPI.bulkEdit({
        employee_ids: selected,
        ...(assignClientIds.length > 0 ? { client_ids: assignClientIds } : {}),
        ...bulkFields,
      });
      toast.success(`${data.updated} employee${data.updated === 1 ? '' : 's'} updated`);
      setAssignOpen(false);
      setSelected([]);
      setAssignClientIds([]);
      setBulkFields({});
      await loadData();
      employeesAPI.companies().then(r => setCompanyOptions(r.data || [])).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk edit failed');
    } finally {
      setAssigning(false);
    }
  };

  const loadData = async () => {
    try {
      const empRes = await employeesAPI.list();
      setEmployees(empRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };
  const companies = ['Security', 'Surveillance', 'Cyber', 'Exquisite', 'Other'];

  const filteredEmployees = employees.filter(emp => {
    const term = search.toLowerCase();
    const matchesSearch = 
      (emp.first_name || '').toLowerCase().includes(term) ||
      (emp.last_name || '').toLowerCase().includes(term) ||
      (emp.employee_number || '').toLowerCase().includes(term) ||
      (emp.email || '').toLowerCase().includes(term);
    const matchesCompany = companyFilter === 'all' || emp.company === companyFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && emp.is_active) ||
      (statusFilter === 'inactive' && !emp.is_active);
    return matchesSearch && matchesCompany && matchesStatus;
  });

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    let aVal, bVal;
    switch (sortKey) {
      case 'last_name': aVal = `${a.last_name} ${a.first_name}`.toLowerCase(); bVal = `${b.last_name} ${b.first_name}`.toLowerCase(); break;
      case 'employee_number': aVal = a.employee_number || ''; bVal = b.employee_number || ''; break;
      case 'company': aVal = (a.company || '').toLowerCase(); bVal = (b.company || '').toLowerCase(); break;
      case 'position': aVal = (a.position || '').toLowerCase(); bVal = (b.position || '').toLowerCase(); break;
      case 'location': aVal = (a.assignment_location || '').toLowerCase(); bVal = (b.assignment_location || '').toLowerCase(); break;
      case 'status': aVal = a.is_active ? 1 : 0; bVal = b.is_active ? 1 : 0; break;
      // Blank hire dates sort last in both directions — an unknown date is not "1970".
      case 'hire_date': {
        const av = (a.hire_date || '').slice(0, 10), bv = (b.hire_date || '').slice(0, 10);
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        aVal = av; bVal = bv;
        break;
      }
      default: aVal = ''; bVal = '';
    }
    if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortHeader = ({ label, colKey }) => (
    <TableHead className="cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => toggleSort(colKey)}>
      <div className="flex items-center gap-1">
        {label}
        {sortKey === colKey ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
      </div>
    </TableHead>
  );

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Employees</h1>
          <p className="text-slate-500 mt-1">
            Manage employee records and assignments
          </p>
        </div>
        <div className="flex gap-3">
          {selected.length > 0 && (
            <RequireCap module="employees" action="edit">
              <Button variant="secondary" onClick={() => setAssignOpen(true)} data-testid="bulk-edit-btn">
                Bulk edit ({selected.length})
              </Button>
            </RequireCap>
          )}
          <RequireCap module="employees" action="export">
            <Button variant="outline" onClick={async () => {
              try {
                const res = await exportAPI.employees();
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = 'employees.csv'; a.click();
              } catch { toast.error('Export failed'); }
            }} data-testid="export-employees-btn">
              <Download className="w-4 h-4 mr-2" />Export CSV
            </Button>
          </RequireCap>
          <RequireCap module="employees" action="add">
            <Button onClick={() => navigate('/employees/new')} data-testid="add-employee-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Employee
            </Button>
          </RequireCap>
        </div>
      </div>

      {/* Filters */}
      {noCoverage && (
        <Card data-testid="employees-no-coverage">
          <CardContent className="py-12 text-center max-w-xl mx-auto">
            <ShieldCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">No client accounts are assigned to you</p>
            <p className="text-sm text-slate-500 mt-3">
              {coverage?.has_employee_record === false
                ? "We couldn't match your sign-in to an employee record, so no client accounts could be resolved. Ask an administrator to link your user account to your employee record."
                : 'Your access is scoped to the client accounts you supervise, and you are not on any account team yet. Once an administrator adds you to a Supervisory Group and attaches that group to your client accounts, the employees at those accounts will appear here.'}
            </p>
          </CardContent>
        </Card>
      )}
      {!noCoverage && (
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input 
                placeholder="Search employees..." 
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="employees-search"
              />
            </div>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="company-filter">
                <SelectValue placeholder="Company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-36" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Table */}
      {!noCoverage && (
      <Card>
        {loading ? (
          <div className="p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 skeleton-pulse rounded"></div>
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={selected.length > 0 && selected.length === sortedEmployees.length}
                    onCheckedChange={(v) => setSelected(v ? sortedEmployees.map(e => e.id) : [])}
                    data-testid="select-all-employees"
                  />
                </TableHead>
                <SortHeader label="Employee" colKey="last_name" />
                <SortHeader label="Employee #" colKey="employee_number" />
                <SortHeader label="Company" colKey="company" />
                <SortHeader label="Position" colKey="position" />
                <SortHeader label="Hire Date" colKey="hire_date" />
                <SortHeader label="Clients" colKey="location" />
                <SortHeader label="Status" colKey="status" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedEmployees.map(emp => (
                <TableRow 
                  key={emp.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/employees/${emp.id}`)}
                  data-testid={`employee-row-${emp.id}`}
                >
                  <TableCell onClick={(e) => e.stopPropagation()} className="w-10">
                    <Checkbox
                      checked={selected.includes(emp.id)}
                      onCheckedChange={() => toggleSelected(emp.id)}
                      data-testid={`select-employee-${emp.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <span className="font-medium text-slate-600">
                          {emp.first_name[0]}{emp.last_name[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{emp.first_name} {emp.last_name}</p>
                        <p className="text-sm text-slate-500">{emp.email || '-'}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono">{emp.employee_number || '-'}</TableCell>
                  <TableCell>{emp.company || '-'}</TableCell>
                  <TableCell>{emp.position || '-'}</TableCell>
                  <TableCell data-testid={`employee-hire-date-${emp.id}`}>
                    {emp.hire_date
                      ? <div>
                          <p className="whitespace-nowrap">{formatHireDate(emp.hire_date)}</p>
                          <p className="text-xs text-slate-500">{tenureLabel(emp.hire_date)}</p>
                        </div>
                      : <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell data-testid={`employee-clients-${emp.id}`}>
                    {(emp.client_ids || []).length > 0
                      ? <span className="text-sm">{(emp.client_ids || []).map(clientName).filter(Boolean).join(', ')}</span>
                      : <span className="text-slate-400">Unassigned</span>}
                  </TableCell>
                  <TableCell>
                    <Badge variant={emp.is_active ? 'default' : 'secondary'}>
                      {emp.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {sortedEmployees.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No employees found</p>
                    {coverage?.is_team_scoped && coverage.client_count > 0 && (
                      <p className="text-sm mt-2" data-testid="employees-team-scope-note">
                        You&apos;re seeing employees at your {coverage.client_count} assigned client
                        account{coverage.client_count === 1 ? '' : 's'} only. Try clearing the filters above.
                      </p>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Card>
      )}

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="max-w-2xl" data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle>Bulk edit {selected.length} employee{selected.length === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription>
              Only the fields you fill in are written — anything left blank stays as it is on each record.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-2">
              <Label className="text-sm">Client accounts <span className="text-fg-muted font-normal">— added to what each person already covers</span></Label>
              <SearchableCombobox
                items={clientList
                  .filter(c => !assignClientIds.includes(c.id))
                  .map(c => ({
                    _key: c.id,
                    _searchStr: `${c.name} ${c.customer_number || ''} ${c.city || ''}`.toLowerCase(),
                    ...c,
                  }))}
                value=""
                onSelect={(v) => v && setAssignClientIds(prev => [...prev, v])}
                placeholder="Add a client..."
                searchPlaceholder="Search client name or number..."
                emptyText="No clients found"
                testId="bulk-assign-client-picker"
                renderItem={(c) => (c ? `${c.name}${c.customer_number ? ` (${c.customer_number})` : ''}` : null)}
              />
              {assignClientIds.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="bulk-assign-client-chips">
                  {assignClientIds.map(cid => (
                    <button key={cid} type="button"
                      onClick={() => setAssignClientIds(prev => prev.filter(x => x !== cid))}
                      className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface-sunken px-3 py-1 text-xs hover:border-danger transition-colors"
                      data-testid={`bulk-assign-client-chip-${cid}`}>
                      {clientName(cid) || cid}<span className="text-fg-muted">×</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Location / Region</Label>
                <Select value={bulkFields.location_id || NO_CHANGE}
                        onValueChange={(v) => setBulkField('location_id', v)}>
                  <SelectTrigger data-testid="bulk-edit-location"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CHANGE}>Leave unchanged</SelectItem>
                    {locationList.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Company</Label>
                <Select value={bulkFields.company || NO_CHANGE}
                        onValueChange={(v) => setBulkField('company', v)}>
                  <SelectTrigger data-testid="bulk-edit-company"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CHANGE}>Leave unchanged</SelectItem>
                    {companyOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Employee Type Description</Label>
                <Select value={bulkFields.post_type || NO_CHANGE}
                        onValueChange={(v) => setBulkField('post_type', v)}>
                  <SelectTrigger data-testid="bulk-edit-post-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CHANGE}>Leave unchanged</SelectItem>
                    {POST_TYPES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm">Classification Description</Label>
                <Select value={bulkFields.classification_description || NO_CHANGE}
                        onValueChange={(v) => setBulkField('classification_description', v)}>
                  <SelectTrigger data-testid="bulk-edit-classification"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CHANGE}>Leave unchanged</SelectItem>
                    {CLASSIFICATIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <p className="text-xs text-fg-muted" data-testid="bulk-edit-summary">
              {changeSummary.length === 0
                ? 'Nothing selected to change yet.'
                : `Will set ${changeSummary.join(', ')} on ${selected.length} employee${selected.length === 1 ? '' : 's'}.`}
            </p>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={submitAssign} disabled={changeSummary.length === 0 || assigning}
              data-testid="bulk-edit-confirm">
              {assigning ? 'Applying…' : `Apply to ${selected.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
